import {
    CULLFACE_NONE,
    SEMANTIC_POSITION,
    BlendState,
    DepthState,
    GraphicsDevice,
    QuadRender,
    RenderPass,
    Shader,
    ShaderUtils,
    StencilParameters,
    Vec4
} from 'playcanvas';

import { resolve } from './resolve';

class ShaderQuad {
    shader: Shader;
    quadRender: QuadRender;

    constructor(device: GraphicsDevice, vertexWGSL: string, fragmentWGSL: string, uniqueName: string, vertexGLSL?: string, fragmentGLSL?: string) {
        this.shader = ShaderUtils.createShader(device, {
            uniqueName,
            attributes: {
                vertex_position: SEMANTIC_POSITION
            },
            vertexWGSL,
            fragmentWGSL,
            // the WebGL2 fallback device compiles these instead
            ...(vertexGLSL && fragmentGLSL ? { vertexGLSL, fragmentGLSL } : {})
        });

        this.quadRender = new QuadRender(this.shader);
    }

    render(viewport?: Vec4, scissor?: Vec4) {
        this.quadRender.render(viewport, scissor);
    }

    destroy() {
        this.shader.destroy();
        this.quadRender.destroy();
    }
}

interface Renderable {
    render(viewport?: Vec4, scissor?: Vec4): void;
}

class SimpleRenderPass extends RenderPass {
    blendState = BlendState.NOBLEND;
    cullMode = CULLFACE_NONE;
    depthState = DepthState.NODEPTH;
    stencilFront: StencilParameters | null = null;
    stencilBack: StencilParameters | null = null;
    viewport: Vec4 | null = null;
    scissor: Vec4 | null = null;

    renderable: Renderable;
    vars: (() => object) | null = null;

    constructor(device: GraphicsDevice, renderable: Renderable, args?: Partial<SimpleRenderPass>) {
        super(device);
        this.renderable = renderable;
        Object.assign(this, args);
    }

    execute(vars: any = {}) {
        const { device, blendState, cullMode, depthState, stencilFront, stencilBack, viewport, scissor } = this;

        if (this.vars) {
            resolve(device.scope, this.vars());
        }

        resolve(device.scope, vars);

        device.setBlendState(blendState);
        device.setCullMode(cullMode);
        device.setDepthState(depthState);
        device.setStencilState(stencilFront ?? undefined, stencilBack ?? undefined);

        this.renderable.render(viewport ?? undefined, scissor ?? undefined);
    }
}

export { ShaderQuad, SimpleRenderPass };
